import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { ErrorMessage, Form, Formik, useFormikContext } from 'formik';
import { Button, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  ButtonFormikTab,
  Checkbox,
  ConfirmModal,
  CustomButton,
  FormTextField,
  ReactSelectForm,
  Section,
} from '../../../../../components';
import { AddIcon } from '../../../../../assets/icons/AddIcon';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { openModal } from '../../../../../features/modal/modalSlice';
import { ModalAction } from '../../../../../types/modal';
import {
  classFormatData,
  classTypesData,
  LegacyTeamLineupDraftModeNote,
  resolveTeamLineupDraftModeForForm,
  getTeamLineupDraftModeDescriptionKey,
  teamLineupDraftModeDataActive,
} from '../../../../../constant/class';
import { boolOption } from '../../../../../constant/setting';

import { TeamSection } from '../TeamSection';
import { ClassRefreshContext } from './ClassRefreshContext';
import { IMatch } from '../../../TournamentPlay/components/PlayTag/PlayTag';
import {
  Group,
  Match,
  User,
  TournamentCategory,
  ProgressionRule,
} from '../../../../../types/tournament';
import { singleClassTagTypeConverter } from '../../../../../utils/classTagTypeConverter';
import { normalizeLoadedTournamentCategories } from '../../../../../utils/tournamentCategoryTemplate';
import { SubmitValidationNotifier } from '../../../../../components/form/SubmitValidationNotifier';
import {
  countActiveCategoryRubbers,
  shouldOfferTiebreakerForClass,
  teamDeciderFormAdjustment,
} from '../../../../../utils/teamCategoryRubberCount';
import { TeamCategoryRecipeTable } from './TeamCategoryRecipeTable';
import { traslateItemWithLabel } from '../../../../../utils/traslation.helpers';
import { SettingBox } from '../../../common/components/SettingBox';
import { selectClass } from '../../../../../features/class/classSelectors';
import { TagIdsEnum } from '../../../../../constant/navigation';
import { selectTournament } from '../../../../../features/tournament/tournamentSelectors';
import { saveProgressionRules, updateClassById } from '../../../../../features/class/classActions';
import { createAndSortOptionsForClassSelect } from '../../../CreativeTourDetail.helpers';
import {
  classByIdUpdateSuccess,
  classDeleteSuccess,
  classSetSingleClass,
  setCurrentName,
  setCurrentType,
} from '../../../../../features/class/classSlice';
import {
  CategoryStanding,
  deleteClass,
  fetchClassFullTeamExtras,
  fetchClassesByTournamentIdAndClassId,
} from '../../../../../apis/tournament';
import { keywordsTypes } from '../../../../adminConfiguration/adminCounfiguration';
import {
  ClassFormatEnum,
  ClassGroupDraw,
  ClassTypeEnum,
  EliminationDrawEnum,
  ConsolationDrawEnum,
  ClassGroupConsolDraw,
  GenderEnum,
  GroupStandingsTieBreakerEnum,
  TeamLineupDraftModeEnum,
  TypeError,
} from '../../../../../types/enums';

import './ClassesTag.scss';
import { generateCategories } from '../../../../../features/tournament/actionCreators';
import { createClassData } from '../../../../../apis/class';
import { InfoIcon } from '../../../../../assets/icons/InfoIcon';
import { Trash, Shuffle } from 'react-bootstrap-icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Notification } from '../../../../../hooks/useNotification';
import { useFindByTour } from '../../../../../generated/orval/admin';
import { getBool } from '../../../../../utils/typeConverters';
import { useGetCurrentOrganization } from '../../../../../features/organizations/organizationsSelectors';
import { getOrganizationTours } from '../../../../../apis/tours';
import { ProgressionSimulatorModal } from '../ProgressionSimulatorModal/ProgressionSimulatorModal';
import { ProgressionRuleBuilderModal } from '../ProgressionSimulatorModal/ProgressionRuleBuilder';
import { GroupStandingsTieBreakerModalControl } from '../../../common/components/GroupStandingsTieBreakerEditor';
import {
  DEFAULT_TEAM_MATCHUP_SCORING,
  hasRequiredGroupStandingsTieBreakers,
  requiredGroupStandingsTieBreakersErrorKey,
  resolveGroupStandingsTieBreakerResolution,
  TeamMatchupScoring,
  toPersistedGroupStandingsTieBreakers,
  toPersistedTeamMatchupScoring,
} from '../../../../../utils/groupStandingsTieBreakers';

//TO DO: ClassValues create some place for all types and interfaces.
export type ClassTeam = {
  readonly id: number;
  name: string;
  users: Array<User>;
  logo: null | string;
  seed: number;
  shuffled?: boolean;
  captain: Array<User>;
};

export interface ClassValues {
  readonly classId: string | number;
  name: string;
  type: number;
  minPlayers: number;
  maxPlayers: number;
  minTeamRoster: number;
  maxTeamRoster: number;
  surface: number;
  ball: number;
  isRanking: string;
  rankingType: string;
  keywordSurface: number;
  keywordBall: number;
  isOnSale: string;
  isPaidClass: boolean;
  isDupr: boolean;
  requiresLicense: boolean;
  requiresDuprPremium: boolean;
  format: number;
  roundScoring: string;
  price: number | null;
  useVat: boolean;
  vat: number | null;
  salesStatus: string;
  stateOfClass: string;
  numberOfGroups: number | string;
  boxLeagueNumOfRounds: number;
  teams: Array<ClassTeam>;
  tournamentGroups: Array<Group>;
  tournamentCategories: Array<TournamentCategory>;
  tournamentMatches: Array<IMatch>;
  eliminationDraw: EliminationDrawEnum;
  consolationDraw: ConsolationDrawEnum;
  isHasSubClasses: boolean;
  parentClassId: number;
  subClasses: ClassValues[];
  gender: GenderEnum | null | number;
  minAge: number | null;
  maxAge: number | null;
  minDupr: number | null;
  maxDupr: number | null;
  moveBox: boolean;
  numberPlayersToBox: number | string;
  progressionRules: any[];
  groupStandingsTieBreakers: GroupStandingsTieBreakerEnum[];
  useInheritedGroupStandingsTieBreakers: boolean;
  teamMatchupScoring: TeamMatchupScoring;
  useInheritedTeamMatchupScoring: boolean;
  groupStandingsRulesVersion?: number;
  groupStandingsTieBreakerResolution?: {
    effective: GroupStandingsTieBreakerEnum[];
    source: 'class' | 'tournament' | 'tour' | 'organization' | 'default';
    chain: {
      class: GroupStandingsTieBreakerEnum[] | null;
      tournament: GroupStandingsTieBreakerEnum[] | null;
      tour: GroupStandingsTieBreakerEnum[] | null;
      organization: GroupStandingsTieBreakerEnum[] | null;
      default: GroupStandingsTieBreakerEnum[];
    };
  };
  seedingType: number;
  autoProgress: boolean | null;
  playBronzeMatch: boolean;
  leaderboardId: string;
  leaderboardFemaleId: string | null;
  devAutoFillTeamUsers: number | null;
  tiebreakerEnabled: boolean;
  groupTiebreakerEnabled: boolean;
  tiebreakerFormat: number | null;
  teamLineupDraftMode: number;
  teamMatchupSlotOrder?: number[] | null;
  categoryStandings?: CategoryStanding[];
  hideGroupStandings?: boolean;
}

const resolveInheritedClassTieBreakers = (
  resolution?: ClassValues['groupStandingsTieBreakerResolution']
): GroupStandingsTieBreakerEnum[] => {
  if (resolution) {
    return (
      resolution.chain.tournament ??
      resolution.chain.tour ??
      resolution.chain.organization ??
      resolution.chain.default
    );
  }

  return resolveGroupStandingsTieBreakerResolution({}).effective;
};

type ClassTieBreakerInput = {
  groupStandingsTieBreakers?: ClassValues['groupStandingsTieBreakers'] | null;
  useInheritedGroupStandingsTieBreakers?: boolean;
  groupStandingsTieBreakerResolution?: ClassValues['groupStandingsTieBreakerResolution'];
  teamMatchupScoring?: TeamMatchupScoring | null;
  useInheritedTeamMatchupScoring?: boolean;
  groupStandingsRulesVersion?: number | null;
};

const normalizeClassTieBreakers = (input: ClassTieBreakerInput) => {
  const inherited = resolveInheritedClassTieBreakers(input.groupStandingsTieBreakerResolution);
  const useInheritedGroupStandingsTieBreakers =
    input.useInheritedGroupStandingsTieBreakers ?? !input.groupStandingsTieBreakers?.length;

  const groupStandingsTieBreakers = useInheritedGroupStandingsTieBreakers
    ? inherited
    : input.groupStandingsTieBreakers?.length
    ? input.groupStandingsTieBreakers
    : inherited;

  const useInheritedTeamMatchupScoring =
    input.useInheritedTeamMatchupScoring ?? input.teamMatchupScoring == null;

  return {
    groupStandingsTieBreakers,
    useInheritedGroupStandingsTieBreakers,
    groupStandingsTieBreakerResolution: input.groupStandingsTieBreakerResolution,
    teamMatchupScoring: input.teamMatchupScoring ?? DEFAULT_TEAM_MATCHUP_SCORING,
    useInheritedTeamMatchupScoring,
    groupStandingsRulesVersion: input.groupStandingsRulesVersion ?? 1,
  };
};

const normalizeClassTournamentMatches = (
  matches: readonly Match[] | readonly IMatch[] | undefined
): IMatch[] =>
  (matches ?? []).map((match) => {
    const court =
      match.court != null && typeof match.court === 'object' ? match.court.id : match.court;
    const extended = match as Match & { isTiebreaker?: boolean; tiebreakerPending?: boolean };
    return {
      ...match,
      court,
      isTiebreaker: Boolean(extended.isTiebreaker),
      tiebreakerPending: Boolean(extended.tiebreakerPending),
    } as IMatch;
  });

const RankingSyncer = ({ tourRankingOptions }: { tourRankingOptions: string[] }) => {
  const { values, setFieldValue } = useFormikContext<ClassValues>();
  useEffect(() => {
    const hasLeaderboard = values.leaderboardId && values.leaderboardId !== '';
    setFieldValue('isRanking', hasLeaderboard ? 'true' : 'false');
    if (hasLeaderboard && !values.rankingType && tourRankingOptions.length > 0) {
      setFieldValue('rankingType', tourRankingOptions[0]);
    }
    // Without a leaderboard there is no ranking type to send — clear it so the backend receives null
    if (!hasLeaderboard && values.rankingType) {
      setFieldValue('rankingType', '');
    }
  }, [values.leaderboardId, values.rankingType, setFieldValue, tourRankingOptions]);
  return null;
};

const TeamDeciderDefaultsSync = () => {
  const { values, setFieldValue } = useFormikContext<ClassValues>();
  const previouslyOffered = useRef<boolean | null>(null);

  const activeRubberCount = countActiveCategoryRubbers(values.tournamentCategories ?? []);
  const canOfferTiebreaker = shouldOfferTiebreakerForClass(values.tournamentCategories ?? []);

  useEffect(() => {
    if (values.type !== ClassTypeEnum.TEAMS) {
      previouslyOffered.current = false;
      return;
    }
    const adjustment = teamDeciderFormAdjustment({
      canOfferTiebreaker,
      previouslyOffered: previouslyOffered.current,
      activeRubberCount,
      tiebreakerEnabled: getBool(values.tiebreakerEnabled),
      groupTiebreakerEnabled: getBool(values.groupTiebreakerEnabled),
    });
    if (
      adjustment?.tiebreakerEnabled !== undefined &&
      getBool(values.tiebreakerEnabled) !== adjustment.tiebreakerEnabled
    ) {
      setFieldValue('tiebreakerEnabled', adjustment.tiebreakerEnabled);
    }
    if (
      adjustment?.groupTiebreakerEnabled !== undefined &&
      getBool(values.groupTiebreakerEnabled) !== adjustment.groupTiebreakerEnabled
    ) {
      setFieldValue('groupTiebreakerEnabled', adjustment.groupTiebreakerEnabled);
    }
    previouslyOffered.current = canOfferTiebreaker;
  }, [
    activeRubberCount,
    canOfferTiebreaker,
    setFieldValue,
    values.groupTiebreakerEnabled,
    values.tiebreakerEnabled,
    values.type,
  ]);

  return null;
};

const GENDER_ANY_SENTINEL = 0;

export const ClassesTag = () => {
  const { t } = useTranslation();
  const prefixValidation = 'validation-';
  const prefix = 'creativeDetail-';
  const inputPrefix = 'input-';
  const buttonPrefix = 'button-';

  const { data } = useAppSelector(selectTournament);
  const { currentClassId, classCollection } = useAppSelector(selectClass);
  const [showSimulation, setShowSimulation] = useState(false);
  const [tieBreakerOpenSignal, setTieBreakerOpenSignal] = useState(0);
  const handleTieBreakerInvalid = useCallback(() => {
    setTieBreakerOpenSignal((count) => count + 1);
  }, []);

  // Fetch leaderboards for the tour this tournament belongs to
  const tourSetting = data?.settings?.tour;
  const tourIdValue = typeof tourSetting === 'object' ? (tourSetting as any)?.id : tourSetting;
  const tourId = tourIdValue ? tourIdValue.toString() : '0';
  const { data: leaderboards, isLoading: isLeaderboardsLoading } = useFindByTour(tourId, {
    query: { enabled: !!tourIdValue },
  });

  // Fetch tour's ranking types from scoring values
  const currentOrganization = useGetCurrentOrganization();
  const { data: toursData } = useQuery({
    queryKey: ['organizationTours', currentOrganization?.id],
    queryFn: () => getOrganizationTours(currentOrganization!.id),
    enabled: !!currentOrganization?.id && !!tourIdValue,
  });

  const tourRankingOptions = useMemo(() => {
    if (!toursData?.items || !tourIdValue) return [];
    const tour = toursData.items.find((t: any) => t.id === Number(tourIdValue));
    if (!tour?.scoringValues?.length) return [];
    try {
      return tour.scoringValues
        .map((sv: string) => {
          const parsed = typeof sv === 'string' ? JSON.parse(sv) : sv;
          return parsed.rankingType != null ? String(parsed.rankingType) : null;
        })
        .filter((v: string | null): v is string => v !== null);
    } catch {
      return [];
    }
  }, [toursData, tourIdValue]);

  const classCollectionMain = useMemo(() => {
    return classCollection.reduce((acc, { ...item }) => {
      if (!item.parentClassId) {
        item.subClasses = classCollection.filter(({ classId, parentClassId }) => {
          return classId === item.classId || item.classId === parentClassId;
        });
        acc.push(item);
      }
      return acc;
    }, [] as ClassValues[]);
  }, [classCollection]);

  const deleteClassMutation = useMutation({
    mutationFn: (id: number) => {
      return deleteClass(id);
    },
    onSuccess: () => {
      Notification(TypeError.success, t(prefix + 'class-deleted'));
    },
    onError: () => {
      Notification(TypeError.error, t(prefix + 'class-delete-error'));
    },
  });

  const { listKeywords } = useAppSelector((state) => state.settings);

  const surfaceOption = listKeywords
    .filter(({ type }) => type === keywordsTypes.SURFACE)
    .map(({ id, name }) => ({ value: id, label: name }));
  const ballOption = listKeywords
    .filter(({ type }) => type === keywordsTypes.BALL)
    .map(({ id, name }) => ({ value: id, label: name }));

  const [currentClass, setCurrentClass] = useState<ClassValues | null>(null);
  const [persistedGroupStandingsRulesVersion, setPersistedGroupStandingsRulesVersion] = useState(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [showTeamSection, setShowTeamSection] = useState<boolean>(false);
  const [legacyDraftModeNote, setLegacyDraftModeNote] =
    useState<LegacyTeamLineupDraftModeNote | null>(null);

  useEffect(() => {
    setShowTeamSection(false);
  }, [currentClassId]);

  const ClassSchema = Yup.object()
    .shape({
      name: Yup.string().required(),
      isOnSale: Yup.string(),
      minPlayers: Yup.number()
        .required(t(prefixValidation + 'required'))
        .typeError(t(prefixValidation + 'mustBeANumber'))
        .min(0, t(prefixValidation + 'minZero')),
      maxPlayers: Yup.number()
        .required(t(prefixValidation + 'required'))
        .typeError(t(prefixValidation + 'mustBeANumber'))
        .positive(t(prefixValidation + 'positive')),
      minTeamRoster: Yup.number().when('type', {
        is: (type: number) => type === ClassTypeEnum.TEAMS,
        then: Yup.number()
          .required(t(prefixValidation + 'required'))
          .typeError(t(prefixValidation + 'mustBeANumber'))
          .min(1, t(prefixValidation + 'positive'))
          .max(20, t(prefixValidation + 'maxRosterLimit')),
      }),
      maxTeamRoster: Yup.number().when('type', {
        is: (type: number) => type === ClassTypeEnum.TEAMS,
        then: Yup.number()
          .required(t(prefixValidation + 'required'))
          .typeError(t(prefixValidation + 'mustBeANumber'))
          .min(1, t(prefixValidation + 'positive'))
          .max(20, t(prefixValidation + 'maxRosterLimit'))
          .test('gte-min-roster', t(prefixValidation + 'maxGteMinRoster'), function (value) {
            const min = Number(this.parent.minTeamRoster);
            return value == null || !Number.isFinite(min) || Number(value) >= min;
          }),
      }),
      price: Yup.number().when('isPaidClass', {
        is: (v: unknown) => getBool(v),
        then: Yup.number()
          .typeError(t(prefixValidation + 'mustBeANumber'))
          .required(t(prefixValidation + 'required'))
          .positive(t(prefixValidation + 'positive'))
          .nullable(true),
      }),
      vat: Yup.number().when('isPaidClass', {
        is: (v: unknown) => getBool(v),
        then: Yup.number()
          .typeError(t(prefixValidation + 'mustBeANumber'))
          .required(t(prefixValidation + 'required'))
          .min(0, t(prefixValidation + 'positive'))
          .nullable(true),
      }),
      minAge: Yup.number()
        .transform((value, originalValue) => {
          return originalValue === '' || originalValue === null ? null : value;
        })
        .typeError(t(prefixValidation + 'mustBeANumber'))
        .min(0, t(prefixValidation + 'positive'))
        .max(120, 'Max 120')
        .nullable(true),
      maxAge: Yup.number()
        .transform((value, originalValue) => {
          return originalValue === '' || originalValue === null ? null : value;
        })
        .typeError(t(prefixValidation + 'mustBeANumber'))
        .min(0, t(prefixValidation + 'positive'))
        .max(120, 'Max 120')
        .nullable(true),
      gender: Yup.mixed().test(
        'gender-singles-validation',
        t(prefixValidation + 'genderSinglesInvalid'),
        function (value) {
          const type = this.parent.type;
          // If type is SINGLES, gender cannot be MIXED
          if (type === ClassTypeEnum.SINGLES && value === GenderEnum.MIXED) {
            return false;
          }
          return true;
        }
      ),
      teams: Yup.array(
        Yup.object({
          name: Yup.string().required(t(prefixValidation + 'required')),
          seed: Yup.number()
            .typeError(t(prefixValidation + 'mustBeANumber'))
            .required(t(prefixValidation + 'required'))
            .min(0, t(prefixValidation + 'positive')),
        })
      ),
      tournamentCategories: Yup.array(
        Yup.object({
          isActive: Yup.boolean().required(t(prefixValidation + 'required')),
          numberOfMatches: Yup.number()
            .typeError(t(prefixValidation + 'mustBeANumber'))
            .required(t(prefixValidation + 'required'))
            .integer(t(prefixValidation + 'integer'))
            .min(0, t(prefixValidation + 'minZero')),
        })
      ),
      groupStandingsTieBreakers: Yup.array().when('useInheritedGroupStandingsTieBreakers', {
        is: false,
        then: Yup.array().test(
          'required-core',
          t('groupStandingsTieBreaker-requiredCoreError'),
          function (value) {
            const list = (value as GroupStandingsTieBreakerEnum[] | undefined) ?? [];
            const isTeamsClass = this.parent.type === ClassTypeEnum.TEAMS;
            if (hasRequiredGroupStandingsTieBreakers(list, { isTeamsClass })) {
              return true;
            }
            return this.createError({
              message: t(requiredGroupStandingsTieBreakersErrorKey(list, { isTeamsClass })),
            });
          }
        ),
      }),
    })
    .test('age-order', 'Min age must be less than or equal to max age', function (values) {
      const { minAge, maxAge } = (values || {}) as { minAge?: unknown; maxAge?: unknown };
      const min =
        minAge === '' || minAge === null || typeof minAge === 'undefined' ? null : Number(minAge);
      const max =
        maxAge === '' || maxAge === null || typeof maxAge === 'undefined' ? null : Number(maxAge);
      if (min === null || max === null || Number.isNaN(min) || Number.isNaN(max)) {
        return true;
      }
      return min <= max;
    });

  const dispatch = useAppDispatch();

  const mainClass = useMemo(
    () =>
      classCollectionMain.find((i) => {
        return i.subClasses.find(
          ({ classId }: { classId: string | number }) => currentClassId === classId
        );
      }),
    [classCollectionMain, currentClassId]
  ) as ClassValues;

  const classOptions = createAndSortOptionsForClassSelect(classCollectionMain);
  const mainClassOptions = useMemo(
    () =>
      createAndSortOptionsForClassSelect(
        (mainClass?.subClasses || []).sort((a: ClassValues, b: ClassValues) =>
          a.classId.toLocaleString().localeCompare(b.classId.toLocaleString())
        )
      ),
    [mainClass]
  );

  const selectInitValues = {
    mainClassId: mainClass?.classId || null,
    classId: currentClassId || null,
  };

  const handleOpenModal = useCallback(() => {
    dispatch(
      openModal({
        currentModal: ModalAction.ADD_TOURNAMENT_CLASS,
        modalOpen: true,
        data: data?.classSettings,
      })
    );
  }, [data, dispatch]);

  const submit = useCallback(
    (values: ClassValues) => {
      if (data && currentClass) {
        const valuesForBackend = {
          ...values,
          gender: (values.gender === GENDER_ANY_SENTINEL
            ? null
            : values.gender) as GenderEnum | null,
        };
        dispatch(
          updateClassById(
            data.id,
            currentClass?.classId,
            singleClassTagTypeConverter(valuesForBackend, classCollection),
            values.format !== currentClass?.format,
            values.isHasSubClasses !== currentClass?.isHasSubClasses ||
              typeof values.classId === 'string'
          )
        );
      }
    },
    [data, dispatch, classCollection, currentClass]
  );

  const getClassData = async (
    classId: number | null = null,
    fieldOverrides?: Partial<ClassValues>
  ) => {
    if (
      typeof data?.id === 'number' &&
      (typeof currentClassId === 'number' || typeof classId === 'number')
    ) {
      try {
        setLoading(true);
        const classIdNum = Number(classId) || Number(currentClassId);
        let classData = await fetchClassesByTournamentIdAndClassId(classIdNum);
        try {
          const extras = await fetchClassFullTeamExtras(classIdNum);
          classData = { ...classData, ...extras };
        } catch {
          // keep class without per-category standings
        }

        dispatch(classSetSingleClass(classData));
        const rawDraftMode =
          (classData as { teamLineupDraftMode?: number }).teamLineupDraftMode ??
          TeamLineupDraftModeEnum.OPEN;
        const { mode: draftMode, legacyNote } = resolveTeamLineupDraftModeForForm(rawDraftMode);
        setLegacyDraftModeNote(legacyNote);
        setPersistedGroupStandingsRulesVersion(
          (classData as { groupStandingsRulesVersion?: number }).groupStandingsRulesVersion ?? 1
        );
        setCurrentClass({
          ...classData,
          classId: classData.id,
          minTeamRoster: classData.minTeamRoster ?? 2,
          maxTeamRoster: classData.maxTeamRoster ?? 4,
          gender: (classData as any).gender ?? GENDER_ANY_SENTINEL,
          isHasSubClasses: Boolean(classData.isHasSubClasses),
          moveBox: Boolean(classData.moveBox),
          autoProgress: Boolean(classData.autoProgress),
          playBronzeMatch: Boolean((classData as any).playBronzeMatch),
          requiresDuprPremium: (classData as any).requiresDuprPremium ?? false,
          seedingType: classData.seedingType,
          numberPlayersToBox: classData.numberPlayersToBox || 1,
          teams: [
            ...classData.teams.filter((i) => Boolean(i.seed)),
            ...classData.teams.filter((i) => !Boolean(i.seed)),
          ]
            .map((i) => ({ ...i, shuffled: false }))
            .filter((t) => {
              if (
                classData.format === ClassFormatEnum.BOX_LEAGUE ||
                classData.format === ClassFormatEnum.BOX_LEAGUE_ELIM
              ) {
                return t.users.length <= 1;
              }
              return true;
            }),
          tournamentCategories: normalizeLoadedTournamentCategories([
            ...classData.tournamentCategories,
          ]),
          isPaidClass: (classData.price || 0) > 0,
          useVat: classData.vat !== 0,
          type: classData.type,
          progressionRules: classData.progressionRules || [],
          ...normalizeClassTieBreakers(classData),
          leaderboardId: classData.leaderboardId ? classData.leaderboardId.toString() : '',
          leaderboardFemaleId:
            classData.leaderboardFemaleId !== null && classData.leaderboardFemaleId !== undefined
              ? classData.leaderboardFemaleId.toString()
              : null,
          tiebreakerEnabled: Boolean(
            (classData as { tiebreakerEnabled?: boolean }).tiebreakerEnabled
          ),
          groupTiebreakerEnabled: Boolean(
            (classData as { groupTiebreakerEnabled?: boolean }).groupTiebreakerEnabled
          ),
          tiebreakerFormat:
            (classData as { tiebreakerFormat?: number | null }).tiebreakerFormat ?? 2,
          teamLineupDraftMode: draftMode,
          teamMatchupSlotOrder: Array.isArray(
            (classData as { teamMatchupSlotOrder?: unknown }).teamMatchupSlotOrder
          )
            ? ((classData as { teamMatchupSlotOrder?: number[] }).teamMatchupSlotOrder ?? null)
            : null,
          tournamentMatches: normalizeClassTournamentMatches(classData.tournamentMatches),
          ...fieldOverrides,
        } as unknown as ClassValues);
        setLoading(false);
      } catch (e) {
        setLoading(false);
      }
    }
  };

  const newClassObj = (mainClassId?: number | string | null) => {
    const targetId = mainClassId ?? currentClassId;
    const classObj = classCollection.find(({ classId }) => classId === targetId);

    if (classObj) {
      const rawDraftMode = classObj.teamLineupDraftMode ?? TeamLineupDraftModeEnum.OPEN;
      const { mode: draftMode, legacyNote } = resolveTeamLineupDraftModeForForm(rawDraftMode);
      setLegacyDraftModeNote(legacyNote);
      setPersistedGroupStandingsRulesVersion(classObj.groupStandingsRulesVersion ?? 1);
      setCurrentClass({
        ...classObj,
        gender: classObj.gender ?? GENDER_ANY_SENTINEL,
        tournamentCategories: normalizeLoadedTournamentCategories(classObj.tournamentCategories),
        tournamentMatches: normalizeClassTournamentMatches(classObj.tournamentMatches),
        ...normalizeClassTieBreakers(classObj),
        teamLineupDraftMode: draftMode,
        teamMatchupSlotOrder: Array.isArray(classObj.teamMatchupSlotOrder)
          ? classObj.teamMatchupSlotOrder
          : null,
        tiebreakerFormat: (classObj as { tiebreakerFormat?: number | null }).tiebreakerFormat ?? 2,
      });
    }

    setLoading(false);
  };

  const handleClassTypeChange = async (type: number) => {
    const teamTiebreakerDefault =
      type === ClassTypeEnum.TEAMS
        ? { tiebreakerEnabled: true, tiebreakerFormat: 2, groupStandingsRulesVersion: 2 }
        : {};
    const generateCategoriesIfNeeded = async () => {
      if (
        type === ClassTypeEnum.TEAMS &&
        data &&
        currentClass &&
        !currentClass.tournamentCategories?.length
      ) {
        setLoading(true);
        if (typeof currentClassId === 'number') {
          await dispatch(
            generateCategories(data.id, currentClassId, {
              ...currentClass,
              teams: [],
              groupStandingsTieBreakers: toPersistedGroupStandingsTieBreakers(
                Boolean(currentClass.useInheritedGroupStandingsTieBreakers),
                currentClass.groupStandingsTieBreakers
              ),
              ...{
                type: ClassTypeEnum.TEAMS,
                isOnSale: currentClass.isOnSale === 'true',
              },
              ...teamTiebreakerDefault,
            })
          );
          getClassData(null, teamTiebreakerDefault);
          setLoading(false);
        } else {
          const classData = await createClassData(data.id, {
            ...currentClass,
            teams: [],
            groupStandingsTieBreakers: toPersistedGroupStandingsTieBreakers(
              Boolean(currentClass.useInheritedGroupStandingsTieBreakers),
              currentClass.groupStandingsTieBreakers
            ),
            ...{
              type: ClassTypeEnum.TEAMS,
              isOnSale: currentClass.isOnSale === 'true',
              gender: currentClass.gender === GENDER_ANY_SENTINEL ? undefined : currentClass.gender,
              roundScoring: currentClass.roundScoring === 'true',
            },
            ...teamTiebreakerDefault,
          });
          dispatch(
            classByIdUpdateSuccess({
              oldId: currentClassId,
              newData: classData,
            })
          );
          await dispatch(generateCategories(data.id, classData.id, classData));
          getClassData(classData.id, teamTiebreakerDefault);
          setLoading(false);
        }
      }
    };
    // Only show modal if we have teams
    if (currentClass && currentClass.teams?.length > 0) {
      // We need to prevent the field from updating here.
      dispatch(
        openModal({
          currentModal: ModalAction.CHANGE_CLASS_TYPE,
          modalOpen: true,
          data: {
            currentClass,
            newType: type,
            onConfirm: (keepTeams: boolean) => {
              if (keepTeams) {
                setCurrentClass({ ...currentClass, type, ...teamTiebreakerDefault });
              } else {
                setCurrentClass({ ...currentClass, teams: [], type, ...teamTiebreakerDefault });
              }
              dispatch(setCurrentType(type));
              generateCategoriesIfNeeded();
            },
          },
        })
      );
    } else if (currentClass) {
      setCurrentClass({ ...currentClass, type, ...teamTiebreakerDefault });
      dispatch(setCurrentType(type));
      generateCategoriesIfNeeded();
    }
  };

  const getFormatOptions = () => {
    let options = classFormatData;

    if (currentClass && currentClass.parentClassId) {
      options = classFormatData.filter(({ isGroup }) => !isGroup);
    }

    if (currentClass && currentClass.type === ClassTypeEnum.DOUBLES) {
      options = options.filter(({ supportsDoubles }) => supportsDoubles);
    }

    if (currentClass && currentClass.type === ClassTypeEnum.TEAMS) {
      options = options.filter(
        ({ value }) =>
          value === ClassFormatEnum.GROUP ||
          value === ClassFormatEnum.GROUP_ONLY ||
          value === ClassFormatEnum.SINGLE_BRACKET
      );
    } else {
      options = options.filter(({ value }) => value !== ClassFormatEnum.GROUP_ONLY);
    }

    return options.map((item) => traslateItemWithLabel(t, item));
  };

  const updateProgressionRule = (
    setFieldValue: any,
    fieldName: string,
    newRules: ProgressionRule[]
  ) => {
    setFieldValue(fieldName, newRules);
    if (currentClassId) {
      dispatch(saveProgressionRules(Number(currentClassId), newRules));
    }
  };

  useEffect(() => {
    if (typeof currentClassId === 'number') {
      getClassData();
    } else {
      newClassObj();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, currentClassId]);

  const allowDeletion = currentClass?.teams?.length === 0;
  // @ts-ignore - id exist when we get it from BE. But not when it just exist in the form locally
  const currentClassExistInBE = currentClass?.id;

  return (
    <div className="px-2 classes-tag">
      <div className="row my-3">
        <div className="col d-flex align-items-center">
          <h4 className="font-weight-bold mb-0">{t(prefix + 'classesTitle')}</h4>
        </div>
        <div className="col d-flex justify-content-end">
          <CustomButton
            cb={handleOpenModal}
            btnText={t(buttonPrefix + 'addClass')}
            className="d-flex justify-content-between align-items-center"
            variant="outline-dark"
          >
            <AddIcon />
          </CustomButton>
        </div>
      </div>
      {mainClass?.subClasses?.length ? (
        <>
          <Formik onSubmit={console.log} enableReinitialize={true} initialValues={selectInitValues}>
            {({ values, setFieldValue }) => {
              return (
                <form>
                  <div className="row-cols-lg-2 d-md-flex">
                    <div className="col-lg-4 col px-0">
                      <ReactSelectForm
                        options={classOptions}
                        name="mainClassId"
                        label={t(prefix + 'className')}
                        onChangeCb={(value: string | number) =>
                          dispatch(setCurrentName(value as unknown as number))
                        }
                      />
                    </div>
                    {mainClassOptions?.length > 1 && (
                      <div className="col-lg-4 col px-0 ms-lg-3 ms-md-3">
                        <ReactSelectForm
                          options={mainClassOptions}
                          name="classId"
                          label={t(prefix + 'subclassSelector')}
                        />
                      </div>
                    )}
                  </div>
                  <div className="d-flex align-items-center justify-content-between my-3">
                    <h4 className="font-weight-bold mb-0">{currentClass?.name}</h4>
                    {currentClassExistInBE &&
                      (allowDeletion ? (
                        <ConfirmModal
                          description={t(prefix + 'class-delete-description')}
                          onConfirm={async () => {
                            await deleteClassMutation.mutateAsync(Number(currentClassId));
                            setCurrentClass(null);
                            const firstRemainingClass = classCollection.find(
                              ({ classId }) => classId !== values.mainClassId
                            );
                            const firstRemainingClassId = firstRemainingClass?.classId
                              ? Number(firstRemainingClass?.classId)
                              : undefined;
                            setFieldValue('classId', firstRemainingClassId);
                            setFieldValue('mainClassId', firstRemainingClassId);
                            if (firstRemainingClassId) {
                              dispatch(setCurrentName(firstRemainingClassId));
                            }
                            dispatch(classDeleteSuccess(Number(currentClassId)));
                          }}
                        >
                          <CustomButton
                            className="d-flex align-items-center"
                            btnText={t(buttonPrefix + 'deleteClass')}
                            variant="outline-dark"
                          >
                            <Trash />
                          </CustomButton>
                        </ConfirmModal>
                      ) : (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{t(prefix + 'class-delete-has-teams')}</Tooltip>}
                        >
                          <span>
                            <CustomButton
                              disabled
                              className="d-flex align-items-center"
                              btnText={t(buttonPrefix + 'deleteClass')}
                              variant="outline-dark"
                            >
                              <Trash />
                            </CustomButton>
                          </span>
                        </OverlayTrigger>
                      ))}
                  </div>
                </form>
              );
            }}
          </Formik>
          {currentClass && (
            <ProgressionSimulatorModal
              show={showSimulation}
              onHide={() => setShowSimulation(false)}
              defaultGroups={Number(currentClass.numberOfGroups) || 1}
              defaultPlayers={currentClass.teams?.length > 0 ? currentClass.teams.length : 20}
              defaultMoveCount={Number(currentClass.numberPlayersToBox) || 1}
            />
          )}
          {(!loading && mainClass && currentClass && (
            <Formik
              key={String(currentClass.classId)}
              validationSchema={ClassSchema}
              enableReinitialize={true}
              validateOnChange={false}
              validateOnBlur={true}
              validateOnMount={false}
              initialValues={currentClass}
              onSubmit={submit}
            >
              {({ values, setFieldValue, errors, touched, setFieldTouched, validateField }) => {
                // Auto-correct gender when type is SINGLES and gender is MIXED
                if (values.type === ClassTypeEnum.SINGLES && values.gender === GenderEnum.MIXED) {
                  setFieldValue('gender', GENDER_ANY_SENTINEL);
                  setFieldTouched('gender', false);
                }

                if (
                  values.numberOfGroups !== '' &&
                  typeof values.numberOfGroups === 'number' &&
                  values.numberOfGroups < 1
                ) {
                  setFieldValue('numberOfGroups', 1);
                } else if (
                  typeof values.numberOfGroups === 'number' &&
                  values.numberOfGroups > 64
                ) {
                  setFieldValue('numberOfGroups', 64);
                }
                const numGroups = Number(values.numberOfGroups) || 1;
                const activeRubberCount = countActiveCategoryRubbers(
                  values.tournamentCategories ?? []
                );
                const canOfferTiebreaker = shouldOfferTiebreakerForClass(
                  values.tournamentCategories ?? []
                );
                const numTeams = values.teams ? values.teams.length : 0;
                const selectedTour = toursData?.items?.find(
                  (tour: { id: number }) => tour.id === Number(tourIdValue)
                ) as
                  | {
                      groupStandingsTieBreakers?: GroupStandingsTieBreakerEnum[] | null;
                      teamMatchupScoring?: TeamMatchupScoring | null;
                    }
                  | undefined;
                const classResolution = resolveGroupStandingsTieBreakerResolution({
                  classTieBreakers: values.useInheritedGroupStandingsTieBreakers
                    ? null
                    : values.groupStandingsTieBreakers,
                  tournamentTieBreakers: data?.settings?.groupStandingsTieBreakers ?? null,
                  tourTieBreakers: selectedTour?.groupStandingsTieBreakers ?? null,
                  organizationTieBreakers: currentOrganization?.groupStandingsTieBreakers ?? null,
                  classTeamMatchupScoring: values.useInheritedTeamMatchupScoring
                    ? null
                    : values.teamMatchupScoring,
                  tournamentTeamMatchupScoring:
                    (
                      data?.settings as
                        | { teamMatchupScoring?: TeamMatchupScoring | null }
                        | undefined
                    )?.teamMatchupScoring ?? null,
                  tourTeamMatchupScoring: selectedTour?.teamMatchupScoring ?? null,
                  organizationTeamMatchupScoring: currentOrganization?.teamMatchupScoring ?? null,
                  isTeamsClass: values.type === ClassTypeEnum.TEAMS,
                  rulesVersion: values.groupStandingsRulesVersion ?? 1,
                });

                const allGenderOptions = [
                  { value: GENDER_ANY_SENTINEL, label: t(prefix + 'genderAny') },
                  { value: GenderEnum.MALE, label: t('enum-male') },
                  { value: GenderEnum.FEMALE, label: t('enum-female') },
                  { value: GenderEnum.MIXED, label: t('enum-mixed') },
                ];

                // Filter out MIXED if type is SINGLES
                const genderOptionsFiltered =
                  values.type === ClassTypeEnum.SINGLES
                    ? allGenderOptions.filter((option) => option.value !== GenderEnum.MIXED)
                    : allGenderOptions;

                return (
                  <Form autoComplete="off" id={TagIdsEnum.CLASSES}>
                    <SubmitValidationNotifier onTieBreakerInvalid={handleTieBreakerInvalid} />
                    <TeamDeciderDefaultsSync />
                    <div className="row-cols-lg-2 d-md-flex">
                      <div className="col-lg-4 col px-0">
                        <FormTextField
                          name="name"
                          placeholder={t(inputPrefix + 'placeholderName')}
                          label={t(prefix + 'name')}
                        />
                      </div>
                      <div className="col-lg-4 col px-0 px-lg-3 px-md-3 px-sm-3">
                        <ReactSelectForm
                          options={classTypesData.map((item) => traslateItemWithLabel(t, item))}
                          name="type"
                          preselect={true}
                          disabled={!!values.parentClassId}
                          updateFormValue={false}
                          onChangeCb={(value: number) => {
                            handleClassTypeChange(value);
                          }}
                          label={t(prefix + 'type')}
                        />
                      </div>
                    </div>

                    {values.type === ClassTypeEnum.TEAMS && (
                      <>
                        {values.tournamentCategories?.length > 0 && (
                          <div className="row py-4 my-4 border-top border-bottom">
                            <div className="row my-3">
                              <div className="col d-flex align-items-center gap-2">
                                <h5 className="font-weight-semibold mb-0">
                                  {t(prefix + 'categoriesTitle')}
                                </h5>
                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip>{t(prefix + 'categoriesIntro')}</Tooltip>}
                                >
                                  <Button variant="link" className="p-0 lh-1">
                                    <InfoIcon width={16} height={16} />
                                  </Button>
                                </OverlayTrigger>
                              </div>
                            </div>

                            <TeamCategoryRecipeTable
                              categories={values.tournamentCategories}
                              parentClassDisabled={!!values.parentClassId}
                              prefix={prefix}
                              format={values.format}
                              groupTiebreakerEnabled={
                                canOfferTiebreaker && getBool(values.groupTiebreakerEnabled)
                              }
                              eliminationTiebreakerEnabled={
                                canOfferTiebreaker && getBool(values.tiebreakerEnabled)
                              }
                            />
                          </div>
                        )}
                        {canOfferTiebreaker &&
                        ((values.format === ClassFormatEnum.GROUP_ONLY &&
                          getBool(values.groupTiebreakerEnabled)) ||
                          (values.format === ClassFormatEnum.SINGLE_BRACKET &&
                            getBool(values.tiebreakerEnabled)) ||
                          (values.format === ClassFormatEnum.GROUP &&
                            (getBool(values.tiebreakerEnabled) ||
                              getBool(values.groupTiebreakerEnabled)))) ? (
                          <div className="row py-3">
                            {values.format === ClassFormatEnum.GROUP_ONLY &&
                              getBool(values.groupTiebreakerEnabled) && (
                                <div className="col-lg-6 px-0">
                                  <ReactSelectForm
                                    options={[
                                      {
                                        value: 1,
                                        label: t(prefix + 'tiebreakerFormatSingles'),
                                      },
                                      {
                                        value: 2,
                                        label: t(prefix + 'tiebreakerFormatDoubles'),
                                      },
                                    ]}
                                    name="tiebreakerFormat"
                                    preselect={true}
                                    label={t(prefix + 'tiebreakerFormat')}
                                    disabled={!!values.parentClassId}
                                  />
                                </div>
                              )}
                            {values.format === ClassFormatEnum.SINGLE_BRACKET &&
                              getBool(values.tiebreakerEnabled) && (
                                <div className="col-lg-6 px-0">
                                  <ReactSelectForm
                                    options={[
                                      {
                                        value: 1,
                                        label: t(prefix + 'tiebreakerFormatSingles'),
                                      },
                                      {
                                        value: 2,
                                        label: t(prefix + 'tiebreakerFormatDoubles'),
                                      },
                                    ]}
                                    name="tiebreakerFormat"
                                    preselect={true}
                                    label={t(prefix + 'tiebreakerFormat')}
                                    disabled={!!values.parentClassId}
                                  />
                                </div>
                              )}
                            {values.format === ClassFormatEnum.GROUP &&
                              (getBool(values.tiebreakerEnabled) ||
                                getBool(values.groupTiebreakerEnabled)) && (
                                <div className="col-lg-4 col-12 px-0">
                                  <ReactSelectForm
                                    options={[
                                      {
                                        value: 1,
                                        label: t(prefix + 'tiebreakerFormatSingles'),
                                      },
                                      {
                                        value: 2,
                                        label: t(prefix + 'tiebreakerFormatDoubles'),
                                      },
                                    ]}
                                    name="tiebreakerFormat"
                                    preselect={true}
                                    label={t(prefix + 'tiebreakerFormat')}
                                    disabled={!!values.parentClassId}
                                  />
                                </div>
                              )}
                          </div>
                        ) : !canOfferTiebreaker && activeRubberCount > 0 ? (
                          <div className="row py-3">
                            <div className="col-12 px-0">
                              <div className="small text-muted">
                                {t(prefix + 'tiebreakerOddRubberHint', {
                                  count: activeRubberCount,
                                })}
                              </div>
                            </div>
                          </div>
                        ) : null}
                        <div className="row py-3 border-top">
                          <div className="col-lg-8 col-12 px-0">
                            <div className="d-flex align-items-center gap-2">
                              <span>{t(prefix + 'teamLineupDraftMode')}</span>
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip>{t(prefix + 'teamLineupDraftModeTooltip')}</Tooltip>
                                }
                              >
                                <Button variant="link" className="p-0">
                                  <InfoIcon width={24} height={24} />
                                </Button>
                              </OverlayTrigger>
                            </div>
                            <ReactSelectForm
                              options={teamLineupDraftModeDataActive.map((item) =>
                                traslateItemWithLabel(t, item)
                              )}
                              name="teamLineupDraftMode"
                              preselect={true}
                              disabled={!!values.parentClassId}
                              withConfirm={(currentClass?.tournamentMatches?.length ?? 0) > 0}
                              confirmTitle={t(prefix + 'draftModeChangeWarning')}
                              confirmDescription={t(prefix + 'draftModeChangeWarningDescription')}
                            />
                            {getTeamLineupDraftModeDescriptionKey(values.teamLineupDraftMode) && (
                              <p className="text-muted small mt-2 mb-0">
                                {t(
                                  getTeamLineupDraftModeDescriptionKey(values.teamLineupDraftMode)!
                                )}
                              </p>
                            )}
                            {legacyDraftModeNote && (
                              <Alert variant="warning" className="mt-2 mb-0">
                                {t(
                                  prefix +
                                    (legacyDraftModeNote === 'simultaneous'
                                      ? 'teamLineupDraftModeSimultaneousMigrated'
                                      : legacyDraftModeNote === 'captainOrder'
                                      ? 'teamLineupDraftModeCaptainOrderUnsupported'
                                      : 'teamLineupDraftModeRosterLockUnsupported')
                                )}
                              </Alert>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                    <div className="row-cols-lg-2 d-sm-flex">
                      <div className="col-lg-4 col px-0">
                        <ReactSelectForm
                          options={getFormatOptions()}
                          name="format"
                          preselect={true}
                          withConfirm={currentClass.tournamentMatches?.length !== 0}
                          label={t(prefix + 'format')}
                        />
                      </div>
                      {(!values.parentClassId &&
                        values.type !== ClassTypeEnum.TEAMS &&
                        (values.format === ClassFormatEnum.GROUP ||
                          values.format === ClassFormatEnum.GROUP_BRACKET) && (
                          <div className="classes-tag__subclass-toggle">
                            <Checkbox name="isHasSubClasses" />
                            <p>{t(prefix + 'subclassToggle')}</p>
                          </div>
                        )) ||
                        null}
                    </div>

                    {((values.type === ClassTypeEnum.TEAMS &&
                      values.format === ClassFormatEnum.GROUP) ||
                      (values.format === ClassFormatEnum.GROUP &&
                        values.type !== ClassTypeEnum.TEAMS) ||
                      values.format === ClassFormatEnum.GROUP_BRACKET) && (
                      <div className="col-lg-8 p-0 mt-2">
                        {t(prefix + 'eliminationdraw')}
                        <ButtonFormikTab
                          currValue={values.eliminationDraw ?? 1}
                          items={ClassGroupDraw.map((item) => traslateItemWithLabel(t, item))}
                          name="eliminationDraw"
                        />
                      </div>
                    )}
                    {values.type !== ClassTypeEnum.TEAMS &&
                      getBool(values.isHasSubClasses) &&
                      (values.format === ClassFormatEnum.GROUP ||
                        values.format === ClassFormatEnum.GROUP_BRACKET) && (
                        <div className="col-lg-8 p-0 mt-2">
                          {t(prefix + 'consolationdraw')}
                          <ButtonFormikTab
                            currValue={values.consolationDraw ?? 1}
                            items={ClassGroupConsolDraw.map((item) =>
                              traslateItemWithLabel(t, item)
                            )}
                            name="consolationDraw"
                          />
                        </div>
                      )}
                    {(values.format === ClassFormatEnum.SINGLE_BRACKET ||
                      (values.format === ClassFormatEnum.GROUP &&
                        !getBool(values.isHasSubClasses))) && (
                      <div className="col-lg-8 p-0 mt-2">
                        {t(prefix + 'playBronzeMatch')}
                        <ButtonFormikTab
                          currValue={String(values.playBronzeMatch)}
                          items={boolOption}
                          name="playBronzeMatch"
                        />
                      </div>
                    )}
                    {!(
                      values.format === ClassFormatEnum.BOX_LEAGUE ||
                      values.format === ClassFormatEnum.BOX_LEAGUE_ELIM ||
                      values.format === ClassFormatEnum.BRACKET ||
                      values.format === ClassFormatEnum.SINGLE_BRACKET ||
                      values.format === ClassFormatEnum.GROUP_ONLY
                    ) && (
                      <div className="row-cols-lg-2 d-sm-flex">
                        <div className="d-flex justify-content-between">
                          <div className="classes-tag__subclass-toggle">
                            <Checkbox name="autoProgress" />
                            <div>
                              <p className="mb-0">{t(prefix + 'autoProgression')}</p>
                              {values.type === ClassTypeEnum.TEAMS ? (
                                <p className="small text-muted mb-0">
                                  {t(prefix + 'autoProgressionTeamHint')}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {(values.format === ClassFormatEnum.GROUP ||
                      values.format === ClassFormatEnum.GROUP_BRACKET ||
                      values.format === ClassFormatEnum.BOX_LEAGUE ||
                      values.format === ClassFormatEnum.GROUP_ONLY ||
                      values.format === ClassFormatEnum.BOX_LEAGUE_ELIM) && (
                      <div className="d-flex justify-content-between">
                        <div
                          className="pe-2"
                          onBlur={() => {
                            if (!values.numberOfGroups) {
                              setFieldValue('numberOfGroups', 1);
                            } else if (isNaN(values.numberOfGroups as number)) {
                              setFieldValue('numberOfGroups', 1);
                            }
                          }}
                        >
                          {!(
                            values.format === ClassFormatEnum.BOX_LEAGUE ||
                            values.format === ClassFormatEnum.BOX_LEAGUE_ELIM
                          ) ? (
                            <FormTextField
                              name="numberOfGroups"
                              pattern="\d*"
                              placeholder={t(inputPrefix + 'numberOfGroups')}
                              label={t(prefix + 'numberOfGroups')}
                            />
                          ) : (
                            <FormTextField
                              name="numberOfGroups"
                              pattern="\d*"
                              placeholder={t(inputPrefix + 'numberOfBoxes')}
                              label={t(prefix + 'numberOfBoxes')}
                            />
                          )}
                        </div>
                      </div>
                    )}
                    {values.format === ClassFormatEnum.BOX_LEAGUE && (
                      <div>
                        <div className="d-flex justify-content-between">
                          <div
                            className="pe-2"
                            onBlur={() => {
                              if (!values.boxLeagueNumOfRounds) {
                                setFieldValue('boxLeagueNumOfRounds', 1);
                              } else if (isNaN(values.boxLeagueNumOfRounds as number)) {
                                setFieldValue('boxLeagueNumOfRounds', 1);
                              }
                            }}
                          >
                            <FormTextField
                              name="boxLeagueNumOfRounds"
                              pattern="\d*"
                              placeholder={t(inputPrefix + 'boxLeagueNumOfRounds')}
                              label={t(prefix + 'boxLeagueNumOfRounds')}
                            />
                          </div>
                        </div>
                        <div className="d-flex justify-content-between">
                          <div className="classes-tag__subclass-toggle">
                            <Checkbox name="moveBox" />
                            <p>{t(prefix + 'moveBox')}</p>
                          </div>
                        </div>
                        {values.moveBox && (
                          <div className="d-flex justify-content-between">
                            <div
                              className="pe-2"
                              onBlur={() => {
                                if (!values.numberPlayersToBox) {
                                  setFieldValue('numberPlayersToBox', 1);
                                } else if (isNaN(values.numberPlayersToBox as number)) {
                                  setFieldValue('numberPlayersToBox', 1);
                                }
                              }}
                            >
                              <FormTextField
                                name="numberPlayersToBox"
                                pattern="\d*"
                                placeholder={t(inputPrefix + 'numberPlayersToBox')}
                                label={t(prefix + 'numberPlayersToBox')}
                              />
                            </div>
                            <div className="mb-3 ms-3">
                              <CustomButton
                                variant="outline-primary"
                                btnText={t(buttonPrefix + 'simulate', 'Simulate')}
                                cb={() => setShowSimulation(true)}
                                className="d-flex align-items-center gap-2"
                              >
                                <Shuffle size={16} />
                              </CustomButton>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {values.format === ClassFormatEnum.BOX_LEAGUE_ELIM && (
                      <div className="mt-4">
                        <ProgressionRuleBuilderModal
                          numberOfGroups={numGroups}
                          defaultPlayers={numTeams}
                          existingRules={values.progressionRules}
                          onRulesChange={(newRules: ProgressionRule[]) => {
                            updateProgressionRule(setFieldValue, 'progressionRules', newRules);
                          }}
                        />
                      </div>
                    )}

                    <div className="d-flex flex-column gap-3">
                      <div className="col-12 col-lg-6 px-0">
                        <div className="d-flex justify-content-between">
                          <div className="w-50 pe-2">
                            <FormTextField
                              name="minPlayers"
                              disabled={!!values.parentClassId}
                              placeholder={t(inputPrefix + 'placeholderNumber')}
                              label={
                                values.type === ClassTypeEnum.TEAMS
                                  ? t(prefix + 'minTeams')
                                  : t(prefix + 'minplayers')
                              }
                            />
                          </div>
                          <div className="w-50 ps-2">
                            <FormTextField
                              name="maxPlayers"
                              disabled={!!values.parentClassId}
                              placeholder={t(inputPrefix + 'placeholderNumber')}
                              label={
                                values.type === ClassTypeEnum.TEAMS
                                  ? t(prefix + 'maxTeams')
                                  : t(prefix + 'maxplayers')
                              }
                            />
                          </div>
                        </div>
                      </div>
                      {values.type === ClassTypeEnum.TEAMS ? (
                        <div className="col-12 col-lg-6 px-0">
                          <div className="d-flex justify-content-between">
                            <div className="w-50 pe-2">
                              <FormTextField
                                name="minTeamRoster"
                                disabled={!!values.parentClassId}
                                placeholder={t(inputPrefix + 'placeholderNumber')}
                                label={
                                  <span className="d-inline-flex align-items-center gap-1">
                                    {t(prefix + 'minRoster')}
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={<Tooltip>{t(prefix + 'minRosterTooltip')}</Tooltip>}
                                    >
                                      <Button variant="link" className="p-0 lh-1">
                                        <InfoIcon width={16} height={16} />
                                      </Button>
                                    </OverlayTrigger>
                                  </span>
                                }
                              />
                            </div>
                            <div className="w-50 ps-2">
                              <FormTextField
                                name="maxTeamRoster"
                                disabled={!!values.parentClassId}
                                placeholder={t(inputPrefix + 'placeholderNumber')}
                                label={
                                  <span className="d-inline-flex align-items-center gap-1">
                                    {t(prefix + 'maxRoster')}
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={<Tooltip>{t(prefix + 'maxRosterTooltip')}</Tooltip>}
                                    >
                                      <Button variant="link" className="p-0 lh-1">
                                        <InfoIcon width={16} height={16} />
                                      </Button>
                                    </OverlayTrigger>
                                  </span>
                                }
                              />
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <div className="col-12 col-lg-6 px-0">
                        <div className="d-flex justify-content-between">
                          <div className="w-50 pe-2">
                            <ReactSelectForm
                              options={surfaceOption}
                              name="keywordSurface"
                              preselect={true}
                              label={t(prefix + 'surface')}
                            />
                          </div>
                          <div className="w-50 ps-2">
                            <ReactSelectForm
                              options={ballOption}
                              name="keywordBall"
                              preselect={true}
                              label={t(prefix + 'ball')}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {!(
                      values.format === ClassFormatEnum.SINGLE_BRACKET ||
                      values.format === ClassFormatEnum.BRACKET
                    ) && (
                      <Section title="Scoring">
                        <div className="py-2">
                          <GroupStandingsTieBreakerModalControl
                            value={
                              values.useInheritedGroupStandingsTieBreakers
                                ? classResolution.effective
                                : values.groupStandingsTieBreakers
                            }
                            onChange={(next) => {
                              setFieldValue('groupStandingsTieBreakers', next);
                              if (values.type === ClassTypeEnum.TEAMS) {
                                setFieldValue('groupStandingsRulesVersion', 2);
                              }
                            }}
                            inheritChecked={values.useInheritedGroupStandingsTieBreakers}
                            onInheritChange={(checked) => {
                              setFieldValue('useInheritedGroupStandingsTieBreakers', checked);
                              if (!checked && values.type === ClassTypeEnum.TEAMS) {
                                setFieldValue('groupStandingsRulesVersion', 2);
                              }
                            }}
                            inheritLabelKey="groupStandingsTieBreaker-useInheritedFromTournament"
                            resolution={classResolution}
                            inheritScope="class"
                            chainContextScope="class"
                            isTeamsClass={values.type === ClassTypeEnum.TEAMS}
                            openSignal={tieBreakerOpenSignal}
                            scoringValue={
                              values.useInheritedTeamMatchupScoring
                                ? classResolution.teamMatchupScoring
                                : values.teamMatchupScoring
                            }
                            onScoringChange={(next) => setFieldValue('teamMatchupScoring', next)}
                            inheritScoringChecked={values.useInheritedTeamMatchupScoring}
                            onInheritScoringChange={(checked) =>
                              setFieldValue('useInheritedTeamMatchupScoring', checked)
                            }
                            inheritScoringLabelKey="teamMatchupScoring-inheritFromTournament"
                            showOptInRankingWarning={
                              values.type === ClassTypeEnum.TEAMS &&
                              persistedGroupStandingsRulesVersion < 2
                            }
                          />
                          <ErrorMessage
                            component="div"
                            name="groupStandingsTieBreakers"
                            className="field-error text-danger fs-7 mt-2"
                          />
                        </div>
                      </Section>
                    )}

                    <Section title="Signup Requirements">
                      <>
                        {values.type === ClassTypeEnum.TEAMS ? (
                          <p className="small text-muted mb-3">
                            {t(prefix + 'teamSignupRequirementsHint')}
                          </p>
                        ) : null}
                        <div className="row-cols-lg-2 d-sm-flex">
                          <div className="col-lg-4 col px-0">
                            <ReactSelectForm
                              options={genderOptionsFiltered}
                              name="gender"
                              label={t(prefix + 'gender')}
                              preselect={false}
                            />
                            <ErrorMessage
                              component="div"
                              name="gender"
                              className="field-error text-danger fs-7"
                            />
                          </div>
                        </div>
                        <div className="row-cols-lg-2 d-sm-flex">
                          <div className="col-lg-4 col px-0">
                            <div className="d-flex justify-content-between">
                              <div className="w-50 pe-2">
                                <FormTextField
                                  name="minAge"
                                  placeholder={t(inputPrefix + 'placeholderNumber')}
                                  label={t(prefix + 'minAge')}
                                />
                              </div>
                              <div className="w-50 ps-2">
                                <FormTextField
                                  name="maxAge"
                                  placeholder={t(inputPrefix + 'placeholderNumber')}
                                  label={t(prefix + 'maxAge')}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <SettingBox title="isDupr">
                          <ButtonFormikTab
                            currValue={values.isDupr}
                            items={boolOption}
                            name="isDupr"
                          />
                        </SettingBox>
                        {getBool(values.isDupr) && (
                          <div className="d-flex justify-content-between">
                            <div className="w-50 pe-2">
                              <FormTextField
                                name="minDupr"
                                placeholder={t(inputPrefix + 'placeholderNumber')}
                                label={t(prefix + 'minDupr')}
                              />
                            </div>
                            <div className="w-50 ps-2">
                              <FormTextField
                                name="maxDupr"
                                placeholder={t(inputPrefix + 'placeholderNumber')}
                                label={t(prefix + 'maxDupr')}
                              />
                            </div>
                          </div>
                        )}
                        {getBool(values.isDupr) && (
                          <SettingBox title="requiresDuprPremium">
                            <ButtonFormikTab
                              currValue={values.requiresDuprPremium}
                              items={boolOption}
                              name="requiresDuprPremium"
                            />
                          </SettingBox>
                        )}
                        <SettingBox title="requiresLicense">
                          <ButtonFormikTab
                            currValue={values.requiresLicense}
                            items={boolOption}
                            name="requiresLicense"
                          />
                        </SettingBox>
                      </>
                    </Section>
                    <Section title="Tour Ranking">
                      <>
                        <RankingSyncer tourRankingOptions={tourRankingOptions} />
                        {leaderboards && leaderboards.length > 0 ? (
                          <>
                            <div className="row-cols-lg-3 d-md-flex">
                              <div className="col-lg-4 col px-0">
                                <div className="d-flex align-items-center gap-2 pt-3">
                                  <span>
                                    {values.leaderboardFemaleId !== null &&
                                    values.leaderboardFemaleId !== undefined
                                      ? t(prefix + 'menLeaderboard')
                                      : t(prefix + 'leaderboard')}
                                  </span>
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip>{t('creativeDetail-leaderboardTooltip')}</Tooltip>
                                    }
                                  >
                                    <Button variant="link" className="p-0">
                                      <InfoIcon width={24} height={24} />
                                    </Button>
                                  </OverlayTrigger>
                                </div>
                                <ReactSelectForm
                                  options={[
                                    { value: '', label: t(prefix + 'notAssigned') },
                                    ...leaderboards.map((lb) => ({
                                      value: lb.id.toString(),
                                      label: lb.name,
                                    })),
                                  ]}
                                  name="leaderboardId"
                                  preselect={false}
                                />
                              </div>
                              {values.leaderboardFemaleId !== null &&
                              values.leaderboardFemaleId !== undefined ? (
                                <>
                                  <div className="col-lg-4 col px-0 px-lg-3 px-md-3">
                                    <span className="d-block pt-3">
                                      {t(prefix + 'womenLeaderboard')}
                                    </span>
                                    <ReactSelectForm
                                      options={[
                                        { value: '', label: t(prefix + 'notAssigned') },
                                        ...leaderboards.map((lb) => ({
                                          value: lb.id.toString(),
                                          label: lb.name,
                                        })),
                                      ]}
                                      name="leaderboardFemaleId"
                                      preselect={false}
                                    />
                                  </div>
                                  <div className="col-lg-4 col px-0 d-flex align-items-end">
                                    <CustomButton
                                      variant="outline-secondary"
                                      btnText={t(prefix + 'mergeLeaderboards')}
                                      cb={() => setFieldValue('leaderboardFemaleId', null)}
                                      className="btn-sm mb-3"
                                    />
                                  </div>
                                </>
                              ) : (
                                values.gender === GenderEnum.MIXED && (
                                  <div className="col-lg-4 col px-0 px-lg-3 px-md-3 d-flex align-items-end">
                                    <CustomButton
                                      variant="outline-secondary"
                                      btnText={t(prefix + 'splitByGender')}
                                      cb={() => setFieldValue('leaderboardFemaleId', '')}
                                      className="btn-sm mb-3"
                                    />
                                  </div>
                                )
                              )}
                            </div>
                          </>
                        ) : tourIdValue && !isLeaderboardsLoading && leaderboards?.length === 0 ? (
                          <p className="text-muted my-3">
                            {t('creativeDetail-noTourLeaderboards')}
                          </p>
                        ) : null}
                        {values.leaderboardId &&
                          values.leaderboardId !== '' &&
                          (tourRankingOptions.length > 0 ? (
                            <div className="d-block d-lg-flex align-items-center my-3">
                              <div className="col-lg-4 col pb-0 pb-lg-3 pb-lg-0 ps-0">
                                <div className="d-flex align-items-center gap-2">
                                  <span>{t(prefix + 'rankingtype')}</span>
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip>{t('creativeDetail-rankingTypeTooltip')}</Tooltip>
                                    }
                                  >
                                    <Button variant="link" className="p-0">
                                      <InfoIcon width={24} height={24} />
                                    </Button>
                                  </OverlayTrigger>
                                </div>
                              </div>
                              <div className="col px-0">
                                <ButtonFormikTab
                                  currValue={values.rankingType || ''}
                                  items={tourRankingOptions}
                                  name="rankingType"
                                />
                              </div>
                            </div>
                          ) : (
                            <p className="text-muted my-3">
                              {t('creativeDetail-noTourRankingTypes')}
                            </p>
                          ))}
                      </>
                    </Section>

                    <Section title="Price Details">
                      <>
                        <div className="d-block d-lg-flex align-items-center my-3">
                          <div className="col-lg-4 col pb-0 pb-lg-3 pb-lg-0 ps-0">
                            <div className="d-flex align-items-center gap-2">
                              <span>{t(prefix + 'onsale')}</span>
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip>{t('creativeDetail-showSalesButtonTooltip')}</Tooltip>
                                }
                              >
                                <Button variant="link" className="p-0">
                                  <InfoIcon width={24} height={24} />
                                </Button>
                              </OverlayTrigger>
                            </div>
                          </div>
                          <div className="col px-0">
                            <div className="settingInputContainer">
                              <ButtonFormikTab
                                currValue={values.isOnSale}
                                items={boolOption}
                                name="isOnSale"
                              />
                            </div>
                          </div>
                        </div>
                        <SettingBox title="PaidClass">
                          <ButtonFormikTab
                            currValue={values.isPaidClass}
                            items={boolOption}
                            name="isPaidClass"
                          />
                        </SettingBox>
                        {getBool(values.isPaidClass) && (
                          <>
                            <SettingBox title="UseVAT">
                              <ButtonFormikTab
                                currValue={values.useVat}
                                items={boolOption}
                                name="useVat"
                              />
                            </SettingBox>
                            {getBool(values.useVat) && (
                              <SettingBox title="VATpercentage">
                                <div className="w-25">
                                  <FormTextField name="vat" id="vat" className="py-0" />
                                </div>
                              </SettingBox>
                            )}
                            <SettingBox title="pricePerTeam">
                              <div className="w-25">
                                <FormTextField
                                  placeholder="€0"
                                  name="price"
                                  id="price"
                                  className="py-0"
                                />
                              </div>
                            </SettingBox>
                          </>
                        )}
                      </>
                    </Section>
                    <ClassRefreshContext.Provider value={getClassData}>
                      <div className="container-fluid px-0">
                        {currentClass?.tournamentMatches?.length > 0 && !showTeamSection ? (
                          <div className="position-relative">
                            <div
                              className="position-absolute w-100 h-100 d-flex flex-column justify-content-center align-items-center"
                              style={{
                                backgroundColor: 'rgba(250, 250, 250, 0.9)',
                                zIndex: 1,
                              }}
                            >
                              <div className="text-center p-4">
                                <h5 className="mb-3">{t(prefix + 'matchesGeneratedWarning')}</h5>
                                <p className="mb-3">
                                  {t(prefix + 'matchesGeneratedWarningDescription')}
                                </p>
                                <CustomButton
                                  variant="outline-dark"
                                  btnText={t(buttonPrefix + 'proceedEditTeams')}
                                  cb={() => setShowTeamSection(true)}
                                />
                              </div>
                            </div>
                            <TeamSection values={values} setField={setFieldValue} />
                          </div>
                        ) : (
                          <TeamSection values={values} setField={setFieldValue} />
                        )}
                      </div>
                    </ClassRefreshContext.Provider>
                  </Form>
                );
              }}
            </Formik>
          )) || <div>Loading...</div>}
        </>
      ) : null}
    </div>
  );
};
